import React, { useState, useEffect } from 'react';
import { Modal, Form, Table, Button } from 'react-bootstrap';
import { useAuth } from '../store/AuthContext';
import { useNavigate } from 'react-router-dom';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import ErrorModalMessage from '../store/ErrorMessage';
import { useIsRTL } from 'react-bootstrap/esm/ThemeProvider';
import {
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';



const EventTemplateComponent = () => {
    const [eventTemplates, setEventTemplates] = useState([]);
    const [currentEventTemplate, setCurrentEventTemplate] = useState({ name: '', location_id: '', description: '' });
    const [showForm, setShowForm] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const { authState } = useAuth();
    const navigate = useNavigate();
    const [locations, setLocations] = useState([]);
    const [ errorMessage, setErrorMessage] = useState(null);



    // Redirect to login if not authenticated
    useEffect(() => {
        if (!authState) {
            navigate('/login');
        }
    }, [authState, navigate]);

    // Fetch all event templates
    useEffect(() => {
        fetchEventTemplates();
        fetchLocations();
    }, []);

    const fetchEventTemplates = async () => {
        const response = await fetch(`${backendURL}/api/event-templates`, {
            method: 'GET',
            credentials: 'include'
        });
        const data = await response.json();
        setEventTemplates(data);
    };

    const fetchLocations = async () => {
        const response = await fetch(`${backendURL}/api/locations`, {
            method: 'GET',
            credentials: 'include',
        })
        if (response.ok){
            const data = await response.json();
            setLocations(data);
        } else {
            setErrorMessage('Unable to fetch locations');
            return;
        }
    }
    const handleFormChange = (e) => {
        setCurrentEventTemplate({ ...currentEventTemplate, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (currentEventTemplate.template_id) {
            await updateEventTemplate(currentEventTemplate.template_id, currentEventTemplate);
        } else {
            await createEventTemplate(currentEventTemplate);
        }
        setCurrentEventTemplate({ name: '', location_id: '', description: '' });
        setShowForm(false);
        fetchEventTemplates();
    };

    const {
        items: visibleEventTemplates,
        resultCount,
        totalCount,
        isFiltered,
        search,
        sort,
        clear,
    } = useListControls(eventTemplates, {
        searchFields: ['name'],
        sortOptions: [
            {
                key: 'name',
                label: 'Name',
                accessor: (template) => template.name?.toLowerCase() ?? '',
            },
        ],
    });


    const createEventTemplate = async (templateData) => {
        await fetch(`${backendURL}/api/event-templates`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(templateData),
            credentials: 'include'
        });
    };

    const updateEventTemplate = async (templateId, templateData) => {
        await fetch(`${backendURL}/api/event-templates/${templateId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(templateData),
            credentials: 'include'
        });
    };

    const deleteEventTemplate = async () => {
        if (templateToDelete) {
            await fetch(`${backendURL}/api/event-templates/${templateToDelete}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            setTemplateToDelete(null);
            setShowModal(false);
            fetchEventTemplates();
        }
    };

    const editEventTemplate = (template) => {
        setCurrentEventTemplate(template);
        setShowForm(true);
    };

    const handleCancel = () => {
        setCurrentEventTemplate({ name: '', location_id: '', description: '' });
        setShowForm(false);
    };

    const getLocationName = (locationId) => {
        const locationObj = locations.find(location => location.location_id === locationId)
        if (locationObj) {
            return(
                <span>{locationObj.name}</span>
            )
        } else {
            return(
                <span>{'Location name failed to load.'}</span>
            )
        }
    }

    return (
        <div>
            <Button onClick={() => setShowForm(!showForm)} className="m-1">
                {showForm ? 'Show Event Templates List' : 'Add New Event Template'}
            </Button>
            <FilterBar
                summary={(
                    <ResultSummary
                        count={resultCount}
                        total={totalCount}
                        noun="template"
                        onClear={clear}
                        isFiltered={isFiltered}
                    />
                )}
            >
                <SearchInput
                    label="Search templates"
                    placeholder="Search by name..."
                    {...search}
                />
                <SortControl {...sort} />
            </FilterBar>
            {showForm ? (
                <Form onSubmit={handleSubmit}>
                    <Form.Group controlId="name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control 
                            type="text"
                            placeholder="Enter event template name"
                            name="name"
                            onChange={handleFormChange}
                            value={currentEventTemplate.name}
                        />
                    </Form.Group>
                    <Form.Group controlId="location_id">
                        <Form.Label>Location</Form.Label>
                        <Form.Select
                            name="location_id"
                            onChange={handleFormChange}
                            value={getLocationName(currentEventTemplate.location_id)}
                        >
                            <option value={''}>{'Select Location'}</option>
                            {locations.map( location => (
                            <option key={location.location_id} value={location.location_id}>{location.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group controlId="description">
                        <Form.Label>Description</Form.Label>
                        <Form.Control 
                            as="textarea"
                            placeholder="Enter event template description"
                            name="description"
                            onChange={handleFormChange}
                            value={currentEventTemplate.description}
                        />
                    </Form.Group>
                    <Button type="submit" className="m-1">Submit</Button>
                    <Button variant="secondary" onClick={handleCancel} className="ml-2">Cancel</Button>
                </Form>
            ) : (
                <div className="scrollable-table">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Location</th>
                            <th>Description</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleEventTemplates.map((template) => (
                            <tr key={template.template_id}>
                                <td>{template.name}</td>
                                <td>{getLocationName(template.location_id)}</td>
                                <td>{template.description}</td>
                                <td>
                                    <Button onClick={() => editEventTemplate(template)} className="m-1"><i className="bi bi-pencil"></i></Button>
                                    <Button variant="danger" onClick={() => {
                                        setTemplateToDelete(template.template_id);
                                        setShowModal(true);
                                    }}><i className="bi bi-trash3"></i></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                </div>
            )}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this event template?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                    <Button variant="danger" onClick={deleteEventTemplate}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            <ErrorModalMessage errorMessage={errorMessage} clearErrorMessage={() => setErrorMessage('')} />
        </div>
    );
    

};

export default EventTemplateComponent;
